/**
 * 공용 리치 텍스트 에디터 (매장 설명글 · 매장 소식).
 *
 * 툴바는 매장 페이지가 실제로 지원하는 마크업으로만 한정한다 —
 * 타이틀(h3, 20px) · 리드(p.qrshop-lead, 16px) · 굵게(strong) · 목록(ul>li) · 번호 목록(ol>li) ·
 * 구분선(hr.qrshop-divider, 위아래 16px) · 이탤릭(em).
 * 강조(span.qrshop-point) · 취소선(s) 버튼은 뺐다. 이미 저장된 글에 있는 것은
 * 지우지 않도록 허용 목록에는 남겨 둔다.
 * 매장 페이지에서 같은 크기로 보이게 하는 인라인 스타일은 저장할 때 qrshop-api 가 붙인다.
 * 붙여넣기는 텍스트로 강제하고, 값을 읽을 때 허용 목록으로 한 번 더 정제한다
 * (서버에서도 wp_kses 로 다시 걸러진다).
 */

// 태그 → 남길 속성
const ALLOWED = {
  P: ['class'],
  BR: [],
  H3: [],
  STRONG: [],
  EM: [],
  S: [],
  UL: ['class'],
  OL: [],
  LI: [],
  HR: ['class'],
  SPAN: ['class'],
};

// 브라우저마다 취소선을 다른 태그로 만든다 — 저장 전에 한 가지로 맞춘다.
// 제목은 타이틀(h3) 하나뿐이라 Pro · 관리자 글의 다른 제목 단계도 h3 으로 맞춘다 (서버도 h4 → h3 으로 연다)
const RENAMED = {
  B: 'STRONG',
  I: 'EM',
  STRIKE: 'S',
  DEL: 'S',
  H1: 'H3',
  H2: 'H3',
  H4: 'H3',
  H5: 'H3',
  H6: 'H3',
};

const POINT_CLASS = 'qrshop-point';

// 리드 문단 · 구분선 표시. p 와 hr 에는 이 클래스만 남긴다 (나머지는 매장 사이트가 붙인다)
const LEAD_CLASS = 'qrshop-lead';
const DIVIDER_CLASS = 'qrshop-divider';

// 내용까지 버릴 태그 (태그만 벗기면 스크립트 문자열이 본문에 남는다)
const DROPPED = ['SCRIPT', 'STYLE', 'IFRAME', 'NOSCRIPT', 'TEMPLATE'];

/** 허용하지 않는 태그·속성을 벗겨 낸다. 태그만 풀고 글자는 남긴다. */
const sanitizeNode = (node) => {
  Array.from(node.childNodes).forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      return;
    }

    if (child.nodeType !== Node.ELEMENT_NODE || DROPPED.includes(child.tagName)) {
      child.remove();
      return;
    }

    // b/i/strike 처럼 브라우저가 만드는 태그는 strong/em/s 로 통일한다
    if (RENAMED[child.tagName]) {
      const replacement = document.createElement(RENAMED[child.tagName].toLowerCase());
      replacement.append(...child.childNodes);
      child.replaceWith(replacement);
      sanitizeNode(replacement);
      return;
    }

    const allowedAttributes = ALLOWED[child.tagName];

    if (!allowedAttributes) {
      // 허용 밖 태그는 자식을 먼저 거른 뒤 자식만 남기고 벗긴다.
      // 부모를 다시 거르면 이 반복이 아직 들고 있는 형제가 먼저 벗겨져 떨어져 나가고,
      // 그 형제 차례에 parentNode 가 null 이라 멈췄다(허용 밖 태그가 둘 이상인 Pro 글 → Simple 전환).
      sanitizeNode(child);
      child.replaceWith(...child.childNodes);
      return;
    }

    Array.from(child.attributes).forEach((attribute) => {
      if (!allowedAttributes.includes(attribute.name)) {
        child.removeAttribute(attribute.name);
      }
    });

    if ('P' === child.tagName) {
      const lead = child.classList.contains(LEAD_CLASS);

      child.removeAttribute('class');

      if (lead) {
        child.className = LEAD_CLASS;
      }
    }

    if ('HR' === child.tagName) {
      child.className = DIVIDER_CLASS;
    }

    // span 은 (예전 글의) 강조 용도만 인정한다
    if ('SPAN' === child.tagName && !child.classList.contains(POINT_CLASS)) {
      sanitizeNode(child);
      child.replaceWith(...child.childNodes);
      return;
    }

    sanitizeNode(child);
  });
};

// 문단 단위 태그. 이것 말고 맨 위에 놓인 글·서식은 문단(<p>)으로 감싼다
const BLOCKS = ['P', 'H3', 'UL', 'OL', 'HR'];

/**
 * 문단 밖 맨 위에 놓인 글·서식을 이어지는 것끼리 <p> 로 감싼다.
 * 커서가 첫 문단 밖에 놓인 채 입력하거나, 전체를 지운 뒤 다시 쓰면 브라우저가
 * 글을 문단 없이 맨 위에 둔다. 그대로 저장하면 매장 페이지에서 문단 여백이 어긋난다.
 */
const wrapLooseInline = (holder) => {
  let paragraph = null;

  Array.from(holder.childNodes).forEach((node) => {
    if (Node.ELEMENT_NODE === node.nodeType && BLOCKS.includes(node.tagName)) {
      paragraph = null;
      return;
    }

    // 문단 사이 줄바꿈 공백은 버린다
    if (!paragraph && Node.TEXT_NODE === node.nodeType && '' === node.data.trim()) {
      node.remove();
      return;
    }

    if (!paragraph) {
      paragraph = document.createElement('p');
      node.before(paragraph);
    }

    paragraph.append(node);
  });
};

const sanitizeHTML = (html) => {
  const holder = document.createElement('div');
  holder.innerHTML = html || '';
  sanitizeNode(holder);
  wrapLooseInline(holder);

  // 커서 자리를 만들려고 넣었던 폭 없는 공백은 저장 전에 지운다
  return holder.innerHTML.replace(/\u200B/g, '').trim();
};

/**
 * 툴바 버튼 — 아이콘(마스크, common.css 의 --editor-icon-*)과 이름.
 * 굵게 · 목록 · 구분선 · 이탤릭은 누구나 아는 모양이라 아이콘만 보이고 이름은 읽기 전용(screen-reader-text)이다.
 * 타이틀 · 리드는 이 서비스만의 개념이라 아이콘 아래에 이름을 같이 보인다(showLabel).
 * 눌림 상태(aria-pressed)는 syncToolbar 가 커서 자리를 보고 켜고 끈다.
 *
 * @param {string} icon --editor-icon-{icon}
 * @param {string} label 이름
 * @param {string} title 툴팁
 * @param {boolean} showLabel 이름을 아이콘 아래에 보일지
 */
const toolbarButton = (icon, label, title, showLabel = false) => {
  const button = document.createElement('button');
  button.type = 'button';
  button.className =
    'qrshop-editor__button' + (showLabel ? ' qrshop-editor__button--labelled' : '');
  button.title = title;
  button.setAttribute('aria-label', label);
  button.setAttribute('aria-pressed', 'false');

  const glyph = document.createElement('span');
  glyph.className = 'qrshop-editor__icon';
  glyph.dataset.icon = icon;
  glyph.setAttribute('aria-hidden', 'true');

  const text = document.createElement('span');
  text.className = showLabel ? 'qrshop-editor__label' : 'screen-reader-text';
  text.textContent = label;

  button.append(glyph, text);

  // 버튼을 눌러도 본문 선택이 풀리지 않게 한다
  button.addEventListener('mousedown', (event) => event.preventDefault());

  return button;
};

/**
 * @param {{ html?: string, placeholder?: string }} options
 * @return {{ element: HTMLElement, getHTML: () => string, setHTML: (html: string) => void }}
 */
const createRichText = ({ html = '', placeholder = '' } = {}) => {
  const container = document.createElement('div');
  container.className = 'qrshop-editor';

  const toolbar = document.createElement('div');
  toolbar.className = 'qrshop-editor__toolbar';

  const area = document.createElement('div');
  area.className = 'qrshop-editor__area';
  area.contentEditable = 'true';
  area.dataset.placeholder = placeholder;
  area.innerHTML = sanitizeHTML(html) || '<p><br></p>';

  /**
   * 빈 칸이면 안내 문구를 띄운다 (data-empty → CSS ::before).
   * 문단 구조(<p><br></p>)로 판단하면 글이 문단 밖에 놓였을 때 비었다고 잘못 보고
   * 안내 문구가 글 위에 그대로 남는다. 그래서 글자가 있는지로 본다.
   * 구분선·목록은 글자가 없어도 모양이 보이므로 빈 칸이 아니다.
   */
  const syncEmpty = () => {
    const blank = '' === area.textContent.replace(/\u200B/g, '').trim();

    area.dataset.empty = blank && !area.querySelector('hr, li') ? 'true' : 'false';
  };

  const focusArea = () => {
    if (!area.contains(document.getSelection()?.anchorNode || null)) {
      area.focus();
    }
  };

  /**
   * execCommand 가 만든 style 붙은 span 을 우리가 허용하는 태그로 바꾼다.
   * 브라우저는 굵게·기울임·취소선을 span[style] 로 만들 때가 있는데,
   * 그 style 은 저장 전 정제에서 지워져 서식이 사라진다.
   */
  const normalizeStyledSpans = () => {
    area.querySelectorAll('span[style]').forEach((span) => {
      const style = span.getAttribute('style') || '';
      let tagName = '';

      if (/line-through/.test(style)) {
        tagName = 's';
      } else if (/italic/.test(style)) {
        tagName = 'em';
      } else if (/font-weight:\s*(bold|[6-9]00)/.test(style)) {
        tagName = 'strong';
      }

      if ('' === tagName) {
        // 알 수 없는 style 은 글자만 남긴다
        span.replaceWith(...span.childNodes);
        return;
      }

      const replacement = document.createElement(tagName);
      replacement.append(...span.childNodes);
      span.replaceWith(replacement);
    });
  };

  const exec = (command, value) => {
    focusArea();
    document.execCommand(command, false, value);
    normalizeStyledSpans();
    area.dispatchEvent(new Event('input', { bubbles: true }));
  };

  /**
   * 선택 영역을 주어진 태그로 감싸거나, 이미 감싸져 있으면 되돌린다.
   *
   * execCommand 의 서식 명령은 브라우저마다 <strike> 나 style 붙은 span 을 만들고
   * 그 style 은 저장 전 정제에서 지워져 서식이 사라진다. 그래서 Range 로 직접 감싼다.
   * 글자를 고르지 않고 눌렀을 때는 빈 태그를 넣고 그 안에 커서를 두어,
   * 이어서 치는 글자가 서식 안으로 들어가게 한다.
   */
  const toggleWrap = (tagName, className = '') => {
    focusArea();

    const selection = document.getSelection();

    if (!selection || 0 === selection.rangeCount) {
      return;
    }

    const range = selection.getRangeAt(0);

    if (!area.contains(range.commonAncestorContainer)) {
      return;
    }

    const target = className ? `${tagName}.${className}` : tagName;
    const from =
      Node.ELEMENT_NODE === range.startContainer.nodeType
        ? range.startContainer
        : range.startContainer.parentElement;
    const existing = from?.closest?.(target);

    // 이미 그 서식 안이면 태그만 벗긴다
    if (existing && area.contains(existing)) {
      existing.replaceWith(...existing.childNodes);
      area.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }

    const wrapper = document.createElement(tagName);

    if (className) {
      wrapper.className = className;
    }

    const next = document.createRange();

    if (range.collapsed) {
      // 폭 없는 공백 하나를 넣어 커서가 머물 자리를 만든다 (정제할 때 지운다)
      wrapper.append(document.createTextNode('\u200B'));
      range.insertNode(wrapper);
      next.setStart(wrapper.firstChild, 1);
      next.collapse(true);
    } else {
      wrapper.append(range.extractContents());
      range.insertNode(wrapper);
      next.selectNodeContents(wrapper);
    }

    selection.removeAllRanges();
    selection.addRange(next);
    area.dispatchEvent(new Event('input', { bubbles: true }));
  };

  // 타이틀 — 이미 h3 면 본문(p)으로 되돌린다
  const title = toolbarButton('title', '타이틀', '선택한 줄을 타이틀(20px)로', true);
  title.addEventListener('click', () => {
    const node = window.getSelection()?.anchorNode;
    const inTitle = node && (node.parentElement || node).closest?.('h3');

    exec('formatBlock', inTitle ? 'p' : 'h3');
  });

  /** 선택이 걸친 맨 위 문단(p · h3). 커서만 있으면 그 줄 하나. */
  const selectedBlocks = () => {
    const selection = document.getSelection();

    if (!selection || 0 === selection.rangeCount) {
      return [];
    }

    const range = selection.getRangeAt(0);

    if (!area.contains(range.commonAncestorContainer)) {
      return [];
    }

    return Array.from(area.children).filter(
      (block) => ['P', 'H3'].includes(block.tagName) && range.intersectsNode(block)
    );
  };

  // 리드 — 고른 문단을 리드로. 모두 이미 리드면 보통 문단으로 되돌린다. 타이틀(h3)은 문단으로 바꿔 적용한다
  const lead = toolbarButton('lead', '리드', '선택한 문단을 리드(16px)로', true);
  lead.addEventListener('click', () => {
    focusArea();

    const blocks = selectedBlocks();

    if (0 === blocks.length) {
      return;
    }

    const allLead = blocks.every(
      (block) => 'P' === block.tagName && block.classList.contains(LEAD_CLASS)
    );

    blocks.forEach((block) => {
      let paragraph = block;

      if ('H3' === block.tagName) {
        paragraph = document.createElement('p');
        paragraph.append(...block.childNodes);
        block.replaceWith(paragraph);
      }

      paragraph.classList.toggle(LEAD_CLASS, !allLead);

      if (0 === paragraph.classList.length) {
        paragraph.removeAttribute('class');
      }
    });

    area.dispatchEvent(new Event('input', { bubbles: true }));
  });

  const bold = toolbarButton('bold', '굵게', '굵게 (⌘B)');
  bold.addEventListener('click', () => exec('bold'));

  const list = toolbarButton('list', '목록', '목록으로 만들기');
  list.addEventListener('click', () => exec('insertUnorderedList'));

  const numbered = toolbarButton('numbered', '번호 목록', '번호 목록으로 만들기');
  numbered.addEventListener('click', () => exec('insertOrderedList'));

  const rule = toolbarButton('rule', '구분선', '구분선 넣기');
  rule.addEventListener('click', () =>
    exec('insertHTML', `<hr class="${DIVIDER_CLASS}"><p><br></p>`)
  );

  const italic = toolbarButton('italic', '이탤릭', '기울임 (⌘I)');
  italic.addEventListener('click', () => toggleWrap('em'));

  toolbar.append(title, lead, bold, list, numbered, rule, italic);

  /** 커서가 든 가장 가까운 목록이 그 종류(UL · OL)면 그 목록 */
  const closestList = (element, tagName) => {
    const found = element.closest('li')?.closest('ul, ol');

    return found?.tagName === tagName ? found : null;
  };

  /**
   * 커서(또는 선택 시작점)가 어떤 서식 안에 있는지 보고 그 버튼을 눌린 상태로 표시한다.
   * 구분선은 상태가 없다. 커서가 편집기 밖이면 모두 끈다.
   */
  const pressed = {
    title: (element) => element.closest('h3'),
    lead: (element) => element.closest(`p.${LEAD_CLASS}`),
    bold: (element) => element.closest('strong'),
    list: (element) => closestList(element, 'UL'),
    numbered: (element) => closestList(element, 'OL'),
    italic: (element) => element.closest('em'),
  };
  const stateButtons = { title, lead, bold, list, numbered, italic };

  const syncToolbar = () => {
    const anchor = document.getSelection()?.anchorNode || null;
    const element =
      anchor && (Node.ELEMENT_NODE === anchor.nodeType ? anchor : anchor.parentElement);
    const inside = element && area.contains(element) ? element : null;

    Object.entries(stateButtons).forEach(([name, button]) => {
      const match = inside ? pressed[name](inside) : null;
      const on = Boolean(match && area.contains(match));
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  };

  // 선택이 바뀔 때마다 — 편집기가 여러 개여도 각자 자기 안인지 보고 표시한다
  document.addEventListener('selectionchange', syncToolbar);
  area.addEventListener('input', syncToolbar);
  toolbar.addEventListener('click', () => window.requestAnimationFrame(syncToolbar));

  /**
   * 빈 서식 태그 바로 뒤에 글자가 붙어 버린 경우 그 글자를 태그 안으로 들여온다.
   * 커서만 두고 서식을 켜면 브라우저(특히 한글 조합 입력)가 새 글자를 태그 밖에
   * 붙이는 일이 있어서, 입력이 있을 때마다 한 번씩 바로잡는다.
   */
  const healEmptyFormats = () => {
    area.querySelectorAll('s, em, strong, span.qrshop-point').forEach((element) => {
      const filled = element.textContent.replace(/\u200B/g, '');
      const next = element.nextSibling;

      if ('' !== filled || !next || Node.TEXT_NODE !== next.nodeType || '' === next.data) {
        return;
      }

      // 텍스트 노드를 그대로 옮기므로 커서 위치는 유지된다
      element.append(next);
    });
  };

  area.addEventListener('input', healEmptyFormats);

  // 리드 문단 끝에서 Enter 를 치면 브라우저가 클래스까지 복사해 다음 줄도 리드가 된다.
  // 새로 생긴 빈 줄이면 보통 문단으로 돌린다 (문장 가운데서 나눈 뒷부분은 리드 그대로 둔다)
  area.addEventListener('input', (event) => {
    if ('insertParagraph' !== event.inputType) {
      return;
    }

    const anchor = document.getSelection()?.anchorNode;
    const block =
      anchor &&
      (Node.ELEMENT_NODE === anchor.nodeType ? anchor : anchor.parentElement)?.closest('p');

    if (
      block &&
      area.contains(block) &&
      block.classList.contains(LEAD_CLASS) &&
      '' === block.textContent.replace(/\u200B/g, '')
    ) {
      block.classList.remove(LEAD_CLASS);
      block.removeAttribute('class');
    }
  });
  area.addEventListener('input', syncEmpty);
  syncEmpty();

  // 붙여넣기는 텍스트만 — 외부 서식·색·표가 들어오지 않게 한다
  area.addEventListener('paste', (event) => {
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') || '';
    document.execCommand('insertText', false, text);
  });

  area.addEventListener('drop', (event) => event.preventDefault());

  container.append(toolbar, area);

  // Enter 로 <p> 가 생기도록 (기본값은 브라우저마다 div/br)
  try {
    document.execCommand('defaultParagraphSeparator', false, 'p');
  } catch (error) {
    // 지원하지 않는 브라우저는 기본 동작을 따른다
  }

  return {
    element: container,
    getHTML: () => {
      const value = sanitizeHTML(area.innerHTML);

      // 빈 문단(리드 포함)만 남은 경우는 빈 값으로 본다
      return /^(<p[^>]*>(\s|<br\s*\/?>)*<\/p>)*$/i.test(value) ? '' : value;
    },
    setHTML: (next) => {
      area.innerHTML = sanitizeHTML(next) || '<p><br></p>';
      syncEmpty();
    },
  };
};

export { createRichText, sanitizeHTML };
